import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — MASCOT actor (the Avatar / "Sticky Academy" YouTube pack).
 *
 * A friendly doodle presenter — thick rounded outlines, flat fills — that REACTS
 * and gestures while a speech bubble carries the point: poses for explain /
 * point / think / cheer / worried / shrug, blinking eyes, and a talking
 * mouth-flap. Pure function of `t` (seconds), deterministic.
 *
 * Director drives it via items = [pose, ...bubbleWords], e.g.
 *   ["think", "What", "is", "RAM?"]
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

export type MascotPose = "explain" | "point" | "think" | "cheer" | "worried" | "shrug";
export interface MascotConfig { pose: MascotPose }

export function parseMascotConfig(text: string): MascotConfig {
  const s = text.toLowerCase();
  const pose: MascotPose =
    /\?|wonder|what if|imagine|consider|think about|ever wondered|question|why do|how does/.test(s) ? "think"
    : /\b(great|amazing|awesome|win|success|congrat|good news|the best|love)\b|!/.test(s) ? "cheer"
    : /\b(careful|warning|danger|problem|mistake|avoid|risk|worried|bad|fail|watch out)\b/.test(s) ? "worried"
    : /\b(look|notice|here|this|see the|point|over here|check out)\b/.test(s) ? "point"
    : /\b(maybe|not sure|depends|either|on the other hand|who knows|hard to say)\b/.test(s) ? "shrug"
    : "explain";
  return { pose };
}

/** Presenter cue — a beat that talks TO the viewer or reacts, good for a mascot. */
export const MASCOT_RE =
  /\bimagine\b|\bconsider\b|think of|think about|ever wondered|let'?s say|meet \w|say hello|picture this|you might|have you ever|here'?s the|the trick is|good news|bad news|watch out|the secret/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

const trunc = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

export const MascotActor: React.FC<VProps> = ({ items, box, theme, t }) => {
  const P = theme.palette;
  const pose = (["explain", "point", "think", "cheer", "worried", "shrug"] as MascotPose[]).includes(items[0] as MascotPose)
    ? (items[0] as MascotPose) : parseMascotConfig(items.join(" ")).pose;
  const bubble = trunc(items.slice(1).join(" ").trim(), 42);

  const u = Math.min(box.h / 40, box.w / 34);
  const ink = theme.dark ? "#e8e4da" : "#20232a";     // thick outline, readable on any bg
  const skin = "#f4c89b";
  const shirt = P.primary;
  const lw = u * 0.55;                                  // outline weight

  // character anchored a bit left so the speech bubble sits upper-right
  const cx = box.cx - 4 * u;
  const headR = 7 * u;
  const headY = box.cy - 6 * u + Math.sin(t * 1.6) * u * 0.35; // idle bob
  const shoulderY = headY + headR + 5.5 * u;

  // blink: eyes shut briefly ~every 3.2s
  const blink = ((t % 3.2) > 3.0) ? 0.12 : 1;
  // talking mouth-flap (closed for "think")
  const talking = pose !== "think";
  const mouthOpen = talking ? (0.5 + 0.5 * Math.sin(t * 9)) : 0;
  const lookUp = pose === "think" ? -u * 1.2 : 0;

  // eyebrow angle by mood
  const browTilt = pose === "worried" ? 10 : pose === "think" ? -6 : pose === "cheer" ? -2 : 0;

  // arm hands per pose (relative to shoulders)
  const armSway = Math.sin(t * 1.6 + 1) * u * 0.4;
  const Lsh = { x: cx - 5 * u, y: shoulderY };
  const Rsh = { x: cx + 5 * u, y: shoulderY };
  const hands: Record<MascotPose, { l: { x: number; y: number }; r: { x: number; y: number } }> = {
    explain: { l: { x: cx - 8 * u, y: shoulderY + 6 * u }, r: { x: cx + 10 * u + armSway, y: shoulderY + 1 * u } },
    point:   { l: { x: cx - 7 * u, y: shoulderY + 6 * u }, r: { x: cx + 12 * u, y: shoulderY - 4 * u } },
    think:   { l: { x: cx - 8 * u, y: shoulderY + 5 * u }, r: { x: cx + 2.5 * u, y: headY + headR - 1 * u } },
    cheer:   { l: { x: cx - 9 * u, y: shoulderY - 7 * u + armSway }, r: { x: cx + 9 * u, y: shoulderY - 7 * u - armSway } },
    worried: { l: { x: cx - 6 * u, y: shoulderY - 2 * u }, r: { x: cx + 6 * u, y: shoulderY - 2 * u } },
    shrug:   { l: { x: cx - 10 * u, y: shoulderY - 1 * u }, r: { x: cx + 10 * u, y: shoulderY - 1 * u } },
  };
  const h = hands[pose];

  const arm = (sh: { x: number; y: number }, hand: { x: number; y: number }) => {
    const mid = { x: (sh.x + hand.x) / 2 + (hand.x - sh.x) * 0.1, y: (sh.y + hand.y) / 2 + 2 * u };
    return `M ${sh.x} ${sh.y} Q ${mid.x} ${mid.y} ${hand.x} ${hand.y}`;
  };

  const mouthPath = () => {
    const my = headY + headR * 0.42;
    if (pose === "cheer") return `M ${cx - 2.4 * u} ${my - u * 0.3} Q ${cx} ${my + 2.4 * u} ${cx + 2.4 * u} ${my - u * 0.3}`;
    if (pose === "worried") return `M ${cx - 2 * u} ${my + u} Q ${cx} ${my - u * 0.8} ${cx + 2 * u} ${my + u}`;
    if (pose === "think") return `M ${cx - 1.4 * u} ${my + u * 0.4} L ${cx + 1.4 * u} ${my + u * 0.1}`;
    return ""; // talking → drawn as an open ellipse below
  };

  return (
    <g>
      {/* speech bubble (upper-right) */}
      {bubble && (() => {
        const bw = Math.max(u * 14, Math.min(box.w * 0.64, (bubble.length + 3) * u * 1.05));
        const bh = 9 * u;
        const bx = cx + 6 * u, by = headY - headR - bh - 1.5 * u;
        const pop = clamp01(t / 0.5);
        return (
          <g opacity={pop} transform={`translate(${cx + 6 * u} ${by + bh}) scale(${0.85 + 0.15 * pop}) translate(${-(cx + 6 * u)} ${-(by + bh)})`}>
            <rect x={bx} y={by} width={bw} height={bh} rx={u * 2} fill={theme.dark ? "#f4f1ea" : "#ffffff"} stroke={ink} strokeWidth={lw * 0.8} />
            <path d={`M ${bx + u * 2} ${by + bh - u * 0.2} l ${-u * 3} ${u * 3} l ${u * 4.5} ${-u * 1.6} Z`} fill={theme.dark ? "#f4f1ea" : "#ffffff"} stroke={ink} strokeWidth={lw * 0.8} />
            <text x={bx + bw / 2} y={by + bh / 2} textAnchor="middle" dominantBaseline="central" fontFamily={theme.body.family} fontWeight={700} fontSize={u * 1.85} fill="#20232a">{bubble}</text>
          </g>
        );
      })()}

      {/* thought "?" for think pose */}
      {pose === "think" && (
        <text x={cx + headR + 3 * u} y={headY - headR + 1 * u} fontFamily={theme.body.family} fontWeight={800} fontSize={u * 5} fill={P.primary} opacity={0.6 + 0.4 * Math.sin(t * 2)}>?</text>
      )}

      {/* arms (behind torso) */}
      <path d={arm(Lsh, h.l)} fill="none" stroke={ink} strokeWidth={lw * 1.7} strokeLinecap="round" />
      <path d={arm(Rsh, h.r)} fill="none" stroke={ink} strokeWidth={lw * 1.7} strokeLinecap="round" />
      <circle cx={h.l.x} cy={h.l.y} r={u * 1.4} fill={skin} stroke={ink} strokeWidth={lw} />
      <circle cx={h.r.x} cy={h.r.y} r={u * 1.4} fill={skin} stroke={ink} strokeWidth={lw} />

      {/* torso */}
      <path d={`M ${cx - 6.5 * u} ${shoulderY + 12 * u}
                Q ${cx - 7.5 * u} ${shoulderY - 1 * u} ${cx - 4.5 * u} ${shoulderY - 2.5 * u}
                Q ${cx} ${shoulderY - 4 * u} ${cx + 4.5 * u} ${shoulderY - 2.5 * u}
                Q ${cx + 7.5 * u} ${shoulderY - 1 * u} ${cx + 6.5 * u} ${shoulderY + 12 * u} Z`}
        fill={shirt} stroke={ink} strokeWidth={lw} />

      {/* neck + head */}
      <rect x={cx - 1.6 * u} y={headY + headR - 1.5 * u} width={3.2 * u} height={4 * u} fill={skin} stroke={ink} strokeWidth={lw} />
      <circle cx={cx} cy={headY} r={headR} fill={skin} stroke={ink} strokeWidth={lw} />
      {/* hair */}
      <path d={`M ${cx - headR} ${headY - 1 * u} Q ${cx - headR} ${headY - headR - 2 * u} ${cx} ${headY - headR - 1.5 * u} Q ${cx + headR} ${headY - headR - 2 * u} ${cx + headR} ${headY - 1 * u} Q ${cx} ${headY - headR + 1 * u} ${cx - headR} ${headY - 1 * u} Z`}
        fill={P.secondary} stroke={ink} strokeWidth={lw} />

      {/* eyes */}
      {[-2.6, 2.6].map((dx, i) => (
        <g key={i}>
          <ellipse cx={cx + dx * u} cy={headY - u * 0.4} rx={u * 1.15} ry={u * 1.25 * blink} fill="#fff" stroke={ink} strokeWidth={lw * 0.7} />
          {blink > 0.5 && <>
            <circle cx={cx + dx * u} cy={headY - u * 0.2 + lookUp} r={u * 0.46} fill={ink} />
            <circle cx={cx + dx * u + u * 0.18} cy={headY - u * 0.42 + lookUp} r={u * 0.16} fill="#fff" />
          </>}
        </g>
      ))}
      {/* eyebrows */}
      {[-2.6, 2.6].map((dx, i) => (
        <line key={`b${i}`} x1={cx + dx * u - u * 1.1} y1={headY - u * 2.1 + (i === 0 ? browTilt : -browTilt) * 0.1 * u}
          x2={cx + dx * u + u * 1.1} y2={headY - u * 2.1 - (i === 0 ? browTilt : -browTilt) * 0.1 * u}
          stroke={ink} strokeWidth={lw} strokeLinecap="round" />
      ))}
      {/* mouth */}
      {talking
        ? <ellipse cx={cx} cy={headY + headR * 0.45} rx={u * 1.5} ry={u * (0.4 + 1.3 * mouthOpen)} fill="#7a2b2b" stroke={ink} strokeWidth={lw * 0.7} />
        : <path d={mouthPath()} fill="none" stroke={ink} strokeWidth={lw} strokeLinecap="round" />}
    </g>
  );
};
